import { useState, useEffect, useRef } from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { FaHamburger } from "react-icons/fa";
import NavbarLogo from "./NavbarLogo";
import NavbarDesktop from "./NavbarDesktop";
import NavbarMobile from "./NavbarMobile";
import { tokens } from "../../styles/Theme/Theme";

// Helper function to determine if a color is light or dark
const isColorLight = (r, g, b) => {
  // Using relative luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// Parse an "rgb(...)"/"rgba(...)" string into {r,g,b}, or null if absent/fully transparent.
const parseRgb = (value) => {
  if (!value) return null;
  const m = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!m) return null;
  const alpha = m[4] !== undefined ? parseFloat(m[4]) : 1;
  if (alpha === 0) return null;
  return { r: parseInt(m[1]), g: parseInt(m[2]), b: parseInt(m[3]) };
};

// Average the color stops of a CSS gradient (read from background-image).
// Sections painted with `background: linear-gradient(...)` have a transparent
// computed backgroundColor, so without this their brightness can't be sampled —
// the navbar would wrongly assume a dark background and use light text over them.
const parseGradient = (backgroundImage) => {
  if (!backgroundImage || backgroundImage === "none") return null;
  const stops = [
    ...backgroundImage.matchAll(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/g),
  ]
    .map((m) => ({
      r: parseInt(m[1]),
      g: parseInt(m[2]),
      b: parseInt(m[3]),
      a: m[4] !== undefined ? parseFloat(m[4]) : 1,
    }))
    .filter((c) => c.a > 0);
  if (stops.length === 0) return null;
  const sum = stops.reduce(
    (acc, c) => ({ r: acc.r + c.r, g: acc.g + c.g, b: acc.b + c.b }),
    { r: 0, g: 0, b: 0 }
  );
  return {
    r: sum.r / stops.length,
    g: sum.g / stops.length,
    b: sum.b / stops.length,
  };
};

// The topmost element under a screen point that is NOT part of the nav itself.
// The nav's own pieces (the paw's hover label hangs below the bar) can sit over
// the sample point and would flip its text tone for the wrong reason. The single
// hit test is the common case; the full stack is only walked when that hit
// landed on the nav.
const elementUnder = (x, y, navEl) => {
  const hit = document.elementFromPoint?.(x, y);
  if (hit && !navEl.contains(hit)) return hit;
  return document.elementsFromPoint(x, y).find((el) => !navEl.contains(el)) || null;
};

// Resolve the effective background color at a screen point by walking up from the
// element under it until an opaque solid color or a gradient is found.
const resolveColorAtPoint = (x, y, navEl) => {
  let el = elementUnder(x, y, navEl);
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const color =
      parseRgb(style.backgroundColor) || parseGradient(style.backgroundImage);
    if (color) return color;
    el = el.parentElement;
  }
  return null;
};

// Read the contrast a section explicitly declares via `data-nav-bg="light|dark"`,
// from the element under the given point up. This is the source of truth for nav
// contrast — robust to section order/height changes (unlike scroll-position
// thresholds), and it lets organically-layered sections (transparent over the body
// gradient, decorative overlays, blobs) state their tone instead of being guessed.
const resolveDeclaredBg = (x, y, navEl) => {
  const declared = elementUnder(x, y, navEl)
    ?.closest("[data-nav-bg]")
    ?.getAttribute("data-nav-bg");
  return declared === "light" || declared === "dark" ? declared : null;
};

// Scroll thresholds. Each pair has a gap between its on and off values
// (hysteresis) so that hovering around one number cannot make the bar flicker.
const SCROLLED_ON = 24; // px from top before the compact glass bar appears
const SCROLLED_OFF = 8; // ...and how close to the top before it goes away
const REVEAL_ZONE = 80; // always shown this close to the top
const HIDE_AFTER_Y = 120; // never auto-hide above this
const HIDE_TRAVEL = 48; // continuous downward travel before hiding
const SHOW_TRAVEL = 24; // continuous upward travel before showing again
const SAMPLE_EVERY = 24; // px of scroll between background-contrast checks...
const SAMPLE_MIN_MS = 100; // ...and never more often than this while scrolling
const SCROLL_END_FALLBACK = 150; // browsers without `scrollend`

// The spacer App.js puts under the nav reserves this much height, and the nav's
// resting layout fills exactly that. See NavbarLogo.jsx for the scrolled geometry.
export const NAV_RESTING_HEIGHT = { xs: 62, md: 132 };
const NAV_BAR_HEIGHT = { xs: 62, md: 68 };

// Selectors for the three states the scroll handler writes onto the <nav>.
// Absent attributes mean the resting state (not scrolled, shown, light tone).
const SCROLLED = "[data-scrolled='true'] &";
const DARK = "[data-tone='dark'] &";

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hamburgerRef = useRef(null);
  const navbarRef = useRef(null);
  const barRef = useRef(null);
  const scrollState = useRef(null);

  // Scroll handling, with no React state at all. Whether the page is scrolled,
  // whether the bar is tucked away, and which tone its text takes are written
  // straight onto the <nav> as data attributes, and every style below keys off
  // them. These used to be three useStates: each flip re-rendered the nav, the
  // logo, the desktop pill and the whole mobile menu, a 100ms+ task on a
  // mid-range phone in the middle of a scroll.
  //
  // Per scroll event: at most one requestAnimationFrame is pending, and an
  // attribute is written only when its value changes. The expensive part -
  // hit-testing and reading computed styles to decide the tone - is skipped
  // while the bar is hidden (nobody can see its tone), and otherwise runs at
  // most every 24px and 100ms, with one final sample when scrolling stops. At
  // 24px alone it degraded to every frame on a brisk scroll.
  useEffect(() => {
    const nav = navbarRef.current;
    if (!nav) return undefined;

    const s = (scrollState.current = {
      frame: 0,
      scrolled: false,
      hidden: false,
      light: true,
      lastY: window.scrollY,
      turnY: window.scrollY, // where the current scroll direction began
      dir: 0,
      sampledY: -Infinity,
      sampledAt: 0,
      endTimer: 0,
    });

    const setLight = (value) => {
      if (value !== s.light) {
        s.light = value;
        nav.setAttribute("data-tone", value ? "light" : "dark");
      }
    };

    const sample = (y) => {
      s.sampledY = y;
      s.sampledAt = performance.now();
      const width = window.innerWidth;

      // At the very top, sample specifically behind the logo, which nests in the
      // white TopCurve blob on the left.
      if (y < 50) {
        const color = resolveColorAtPoint(
          nav.offsetLeft + 100,
          nav.offsetHeight + 10,
          nav
        );
        setLight(color ? isColorLight(color.r, color.g, color.b) : true);
        return;
      }

      // Past the top blob, the section under the bar declares its own tone via
      // `data-nav-bg="light|dark"`.
      const sampleY = (barRef.current?.offsetHeight || 68) + 20;
      const declared = resolveDeclaredBg(width / 2, sampleY, nav);
      if (declared) {
        setLight(declared === "light");
        return;
      }

      // Untagged content (e.g. other routes): sample the real background colors.
      const colors = [0.2, 0.5, 0.8]
        .map((f) => resolveColorAtPoint(width * f, sampleY, nav))
        .filter(Boolean);
      if (colors.length > 0) {
        const lightCount = colors.filter((c) => isColorLight(c.r, c.g, c.b)).length;
        setLight(lightCount > colors.length / 2);
        return;
      }

      // Default: transparent sections sit over the colourful brand gradient.
      setLight(false);
    };

    const update = (forceSample = false) => {
      s.frame = 0;
      const y = window.scrollY;

      const nextScrolled = s.scrolled ? y > SCROLLED_OFF : y > SCROLLED_ON;
      if (nextScrolled !== s.scrolled) {
        s.scrolled = nextScrolled;
        nav.setAttribute("data-scrolled", String(nextScrolled));
      }

      // Auto-hide: slide the bar away while reading down, bring it back on a
      // deliberate scroll up. Measured as travel since the last change of
      // direction, so trackpad jitter of a few pixels either way does nothing.
      const delta = y - s.lastY;
      if (delta !== 0) {
        const dir = Math.sign(delta);
        if (dir !== s.dir) {
          s.dir = dir;
          s.turnY = s.lastY;
        }
      }
      s.lastY = y;

      let nextHidden = s.hidden;
      if (y < REVEAL_ZONE) nextHidden = false;
      else if (s.dir > 0 && y > HIDE_AFTER_Y && y - s.turnY >= HIDE_TRAVEL) nextHidden = true;
      else if (s.dir < 0 && s.turnY - y >= SHOW_TRAVEL) nextHidden = false;
      // Never pull the bar away from someone using it: keyboard focus inside, or
      // the pointer resting on it.
      if (nextHidden && nav.matches(":focus-within, :hover")) nextHidden = false;

      // Sample before a hidden bar comes back, so it never shows the wrong tone.
      const unhiding = s.hidden && !nextHidden;
      const due =
        Math.abs(y - s.sampledY) >= SAMPLE_EVERY &&
        performance.now() - s.sampledAt >= SAMPLE_MIN_MS;
      if (!nextHidden && (forceSample || unhiding || due)) sample(y);

      if (nextHidden !== s.hidden) {
        s.hidden = nextHidden;
        nav.setAttribute("data-hidden", String(nextHidden));
      }
    };

    const onForce = () => {
      cancelAnimationFrame(s.frame);
      s.frame = requestAnimationFrame(() => update(true));
    };
    const onScroll = () => {
      if (!s.frame) s.frame = requestAnimationFrame(() => update());
      if (!("onscrollend" in window)) {
        clearTimeout(s.endTimer);
        s.endTimer = setTimeout(onForce, SCROLL_END_FALLBACK);
      }
    };

    // The first sample waits for the frame after mount rather than forcing a
    // layout inside the task that renders the page. The resting defaults (not
    // scrolled, light tone) are already right at the top of every page.
    onForce();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scrollend", onForce, { passive: true });
    window.addEventListener("resize", onForce, { passive: true });
    // Fired by PageTransition once a new route has faded in: the page under the
    // bar changed without any scrolling.
    window.addEventListener("page:entered", onForce);

    return () => {
      cancelAnimationFrame(s.frame);
      clearTimeout(s.endTimer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scrollend", onForce);
      window.removeEventListener("resize", onForce);
      window.removeEventListener("page:entered", onForce);
    };
  }, []);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
      // Never leave the bar/hamburger hidden around the menu.
      const s = scrollState.current;
      if (s && s.hidden) {
        s.hidden = false;
        navbarRef.current?.setAttribute("data-hidden", "false");
      }
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Handle mobile menu close
  const handleCloseMobileMenu = () => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      hamburgerRef.current?.focus();
    }, 300);
  };

  // The nav is fixed and never changes size. Its resting layout is exactly the
  // height of the spacer App.js reserves, so the page starts where it always
  // did; the compact scrolled look is transforms and opacity on top of that
  // (logo in NavbarLogo, the pill and hamburger below, the glass bar here).
  // Only transform and opacity ever animate, so nothing about scrolling can
  // re-lay-out the page. The box itself ignores the pointer - its lower part
  // is transparent once the bar is compact - and each control opts back in.
  const navbarStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    pointerEvents: "none",
    // Auto-hide: slide fully out of view (relative to own height) when hidden.
    transform: "translateY(0)",
    "&[data-hidden='true']": { transform: "translateY(-100%)" },
    transition: `transform ${tokens.transitions.normal}`,
    pr: { xs: 2, md: 4, lg: 8 },
    // Extra LEFT padding insets the logo into the white curve (whose left edge
    // curves rightward as it descends), like the legacy header.
    pl: { xs: 6, md: 13, lg: 14 },
    pt: { xs: 1, md: 2 },
    pb: { xs: 1, md: 2 },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
  };

  // The glass bar's two tints. Over a light section the bar is near-white with
  // dark text; over the gradient it is a faint glass with light text. Both are
  // layers inside the blurred bar and the tone change crossfades their opacity,
  // rather than transitioning background-color, which repainted the blurred
  // surface on every frame of the fade.
  const tint = {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    transition: `opacity ${tokens.transitions.normal}`,
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  };

  // Hamburger button styles
  const hamburgerStyles = {
    display: { lg: "none" },
    pointerEvents: "auto",
    color: tokens.colors.background.dark,
    padding: "10px",
    border: "1px solid rgba(0, 0, 0, 0.15)",
    borderRadius: tokens.borderRadius.md,
    transition: `color ${tokens.transitions.fast}, background-color ${tokens.transitions.fast}, border-color ${tokens.transitions.fast}, transform ${tokens.transitions.normal}`,
    // Recentred in the compact bar once scrolled (132px resting -> 68px bar).
    [SCROLLED]: { transform: { md: "translateY(-32px)" } },
    "&:hover": {
      background: "rgba(0, 0, 0, 0.05)",
    },
    [DARK]: {
      color: tokens.colors.text.primary,
      borderColor: tokens.colors.background.glassBorder,
      "&:hover": { background: tokens.colors.background.glass },
    },
    // Filled burger inherits the nav text color and animates on hover/press
    "& .burger": {
      transformOrigin: "center",
      transition: "transform 0.25s cubic-bezier(0.2, 0.8, 0.3, 1.4)",
    },
    // Playful burger: lift + tilt on hover, squish on press
    "&:hover .burger": {
      transform: "translateY(-2px) rotate(-6deg) scale(1.08)",
    },
    "&:active .burger": {
      transform: "translateY(0) scale(0.92)",
    },
    "&:focus-visible": {
      outline: `2px solid ${tokens.colors.brand.blue}`,
      outlineOffset: "2px",
    },
  };

  return (
    <>
      <Box
        ref={navbarRef}
        component="nav"
        sx={navbarStyles}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* The compact glass bar. Fades in behind the controls once scrolled;
            hidden outright (not just transparent) at rest so its backdrop blur
            is not composited over the hero for nothing. */}
        <Box
          ref={barRef}
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: NAV_BAR_HEIGHT,
            zIndex: -1,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            opacity: 0,
            visibility: "hidden",
            pointerEvents: "none",
            transition: `opacity ${tokens.transitions.normal}, visibility 0s linear 300ms`,
            [SCROLLED]: {
              opacity: 1,
              visibility: "visible",
              // Once visible it is a surface: a click on its empty stretch should
              // land on the bar, not on whatever page content is hidden beneath it.
              pointerEvents: "auto",
              transition: `opacity ${tokens.transitions.normal}`,
            },
            "&::before": {
              ...tint,
              background: "rgba(255, 255, 255, 0.85)",
              borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.08)",
            },
            "&::after": {
              ...tint,
              opacity: 0,
              background: tokens.colors.background.glass,
              borderBottom: `1px solid ${tokens.colors.background.glassBorder}`,
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.15)",
            },
            [`${DARK}::before`]: { opacity: 0 },
            [`${DARK}::after`]: { opacity: 1 },
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />

        {/* Logo - clicks to home */}
        <NavbarLogo />

        {/* Desktop Navigation */}
        {!isMobile && <NavbarDesktop />}

        {/* Mobile Hamburger Button */}
        {isMobile && (
          <IconButton
            ref={hamburgerRef}
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            sx={hamburgerStyles}
          >
            <FaHamburger className="burger" size={24} />
          </IconButton>
        )}
      </Box>

      {/* Mobile Full-Screen Overlay */}
      {isMobile && (
        <NavbarMobile open={mobileMenuOpen} onClose={handleCloseMobileMenu} />
      )}
    </>
  );
};

export default Navbar;
